import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ContactUs } from './../../shared/services/contact-us/contact-us';
import { ContactUsService } from './../../shared/services/contact-us/contact-us.service';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { ErrorComponent, Helpers, InformationComponent } from 'src/app/shared/util/helpers';
import { catchError, filter, finalize, map, switchMap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { BlogService } from 'src/app/shared/services/blog/blog.service';
import { Blog } from 'src/app/shared/services/blog/blog';
import { BlogItemCommentService } from 'src/app/shared/services/blog-item-comment/blog-item-comment.service';
import { TrackerService } from 'src/app/shared/util/tracker.service';
import { SUBSCRIPTION_PRICES, SubscriptionPriceEnum } from 'src/app/shared/util/subscription-prices.enum';

export interface HeroSlide {
  titleKey: string;
  subtitleKey: string;
  image: string;
}

export interface FaqItem {
  questionKey: string;
  answerKey: string;
}

export interface SolutionCard {
  icon: string;
  titleKey: string;
  textShortKey: string;
  route: string;
}

export interface ResourceCard {
  icon: string;
  titleKey: string;
  textShortKey: string;
  route: string;
}

/** Sticky header (~4rem) + small gap */
const MAIN_SCROLL_HEADER_OFFSET = 80;

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss']
})
export class MainComponent implements OnInit, AfterViewInit, OnDestroy {
  public contactItem: ContactUs = new ContactUs();
  public assistantHref = '';
  public latestBlogItems: Blog[] = [];
  subscriptionPrices = SUBSCRIPTION_PRICES;

  heroSlideIndex = 0;
  openFaqIndex: number | null = 0;
  contactSuccess = false;
  isLoading = false;

  private heroIntervalId: ReturnType<typeof setInterval> | null = null;
  private routerEventsSub: Subscription | null = null;
  private fragmentScrollTimer: ReturnType<typeof setTimeout> | null = null;

  readonly heroSlides: HeroSlide[] = [
    { titleKey: 'HOME.MAIN_TITLE1', subtitleKey: 'HOME.MAIN_SUBTITLE1', image: 'assets/images/slider/1.png' },
    { titleKey: 'HOME.MAIN_TITLE2', subtitleKey: 'HOME.MAIN_SUBTITLE2', image: 'assets/images/slider/2.png' },
    { titleKey: 'HOME.MAIN_TITLE3', subtitleKey: 'HOME.MAIN_SUBTITLE3', image: 'assets/images/slider/3.png' },
    { titleKey: 'HOME.MAIN_TITLE4', subtitleKey: 'HOME.MAIN_SUBTITLE4', image: 'assets/images/slider/4.png' },
    { titleKey: 'HOME.MAIN_TITLE5', subtitleKey: 'HOME.MAIN_SUBTITLE5', image: 'assets/images/slider/5.png' },
  ];

  readonly faqItems: FaqItem[] = [
    { questionKey: 'HOME.FEATURE_QUESTION1_TITLE', answerKey: 'HOME.FEATURE_QUESTION1_TEXT' },
    { questionKey: 'HOME.FEATURE_QUESTION2_TITLE', answerKey: 'HOME.FEATURE_QUESTION2_TEXT' },
    { questionKey: 'HOME.FEATURE_QUESTION3_TITLE', answerKey: 'HOME.FEATURE_QUESTION3_TEXT' },
    { questionKey: 'HOME.FEATURE_QUESTION4_TITLE', answerKey: 'HOME.FEATURE_QUESTION4_TEXT' },
    { questionKey: 'HOME.FEATURE_QUESTION5_TITLE', answerKey: 'HOME.FEATURE_QUESTION5_TEXT' },
  ];

  readonly solutions: SolutionCard[] = [
    { icon: 'lni-pulse', titleKey: 'HOME.FEATURE_AUTOMATE_OPERATIONS_TITLE', textShortKey: 'HOME.FEATURE_AUTOMATE_OPERATIONS_TEXT_SHORT', route: '/solution1' },
    { icon: 'lni-wallet', titleKey: 'HOME.FEATURE_COLLECT_PAYMENTS_ONLINE_TITLE', textShortKey: 'HOME.FEATURE_COLLECT_PAYMENTS_ONLINE_TEXT_SHORT', route: '/solution2' },
    { icon: 'lni-bar-chart', titleKey: 'HOME.FEATURE_PROFESSIONAL_REPORT_TITLE', textShortKey: 'HOME.FEATURE_PROFESSIONAL_REPORT_TEXT_SHORT', route: '/solution3' },
    { icon: 'lni-shield', titleKey: 'HOME.FEATURE_SCORING_TITLE', textShortKey: 'HOME.FEATURE_SCORING_TEXT_SHORT', route: '/solution4' },
    { icon: 'lni-files', titleKey: 'HOME.FEATURE_INVENTORY_TITLE', textShortKey: 'HOME.FEATURE_INVENTORY_TEXT_SHORT', route: '/solution5' },
    { icon: 'lni-bolt', titleKey: 'HOME.FEATURE_UTILITIES_TITLE', textShortKey: 'HOME.FEATURE_UTILITIES_TEXT_SHORT', route: '/solution6' },
  ];

  readonly resources: ResourceCard[] = [
    { icon: 'lni-book', titleKey: 'HOME.FISCAL_GUIDE_TITLE', textShortKey: 'HOME.FISCAL_GUIDE_TEXT_SHORT', route: '/fiscal-guide' },
    { icon: 'lni-support', titleKey: 'HOME.HELP_CENTER_TITLE', textShortKey: 'HOME.HELP_CENTER_TEXT_SHORT', route: '/help' },
    { icon: 'lni-dashboard', titleKey: 'HOME.MANAGEMENT_TOOL_TITLE', textShortKey: 'HOME.MANAGEMENT_TOOL_TEXT_SHORT', route: '/management-tool' },
    { icon: 'lni-files', titleKey: 'HOME.DOCUMENTS_AND_TEMPLATES_TITLE', textShortKey: 'HOME.DOCUMENTS_AND_TEMPLATES_TEXT_SHORT', route: '/documents' },
    { icon: 'lni-calculator', titleKey: 'HOME.CALCULATORS_TITLE', textShortKey: 'HOME.CALCULATORS_TEXT_SHORT', route: '/calculators' },
    { icon: 'lni-checkbox', titleKey: 'HOME.TODO_LISTS_TITLE', textShortKey: 'HOME.TODO_LISTS_TEXT_SHORT', route: '/todo' },
  ];

  constructor(
    public snackBar: MatSnackBar,
    public contactUsService: ContactUsService,
    public blogService: BlogService,
    private router: Router,
    private blogItemCommentService: BlogItemCommentService,
    public translate: TranslateService,
    private trackerService: TrackerService
  ) {}

  ngOnInit(): void {
    this.contactItem.subject = '';
    this.contactItem.content = '';
    this.contactItem.email = '';
    this.contactItem.telephone = '';
    this.assistantHref = environment.assistantApp;
    this.trackerService.trackPageViews();

    this.routerEventsSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        const fragment = this.getUrlFragment();
        if (fragment) {
          this.scheduleScrollToFragment(fragment);
        }
      });

    const initialFragment = this.getUrlFragment();
    if (initialFragment) {
      this.scheduleScrollToFragment(initialFragment);
    }

    this.heroIntervalId = setInterval(() => {
      this.heroSlideIndex = (this.heroSlideIndex + 1) % this.heroSlides.length;
    }, 6000);

    Helpers.getObservable([])
      .pipe(
        switchMap(() => this.blogService.getLastThreeItems()),
        switchMap((blogItemList: Blog[]) => {
          const blogItemsWithComments$ = blogItemList.map((item) =>
            this.blogItemCommentService.getCommentsByBlogItem(item.id).pipe(
              map((comments) => ({ ...item, commentsCount: comments ? comments.length : 0 }))
            )
          );
          return Helpers.getObservable(blogItemsWithComments$);
        }),
        catchError((error) => {
          throw error;
        }),
        finalize(() => {})
      )
      .subscribe(
        (results) => {
          this.latestBlogItems = results || [];
        },
        () => {}
      );
  }

  ngAfterViewInit(): void {
    const fragment = this.getUrlFragment();
    if (fragment) {
      this.scheduleScrollToFragment(fragment);
    }
  }

  ngOnDestroy(): void {
    if (this.heroIntervalId) {
      clearInterval(this.heroIntervalId);
    }
    this.routerEventsSub?.unsubscribe();
    if (this.fragmentScrollTimer) {
      clearTimeout(this.fragmentScrollTimer);
    }
  }

  private getUrlFragment(): string | null {
    return this.router.parseUrl(this.router.url).fragment;
  }

  private scheduleScrollToFragment(fragment: string): void {
    if (this.fragmentScrollTimer) {
      clearTimeout(this.fragmentScrollTimer);
    }
    this.fragmentScrollTimer = setTimeout(() => this.scrollToFragment(fragment, 0), 50);
  }

  private scrollToFragment(fragment: string, attempt: number): void {
    const target = document.getElementById(fragment);
    if (target) {
      const top =
        target.getBoundingClientRect().top + window.pageYOffset - MAIN_SCROLL_HEADER_OFFSET;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: attempt === 0 ? 'smooth' : 'auto',
      });
      return;
    }

    if (attempt < 20) {
      setTimeout(() => this.scrollToFragment(fragment, attempt + 1), 100);
    }
  }

  setHeroSlide(index: number): void {
    this.heroSlideIndex = index;
  }

  toggleFaq(index: number): void {
    this.openFaqIndex = this.openFaqIndex === index ? null : index;
  }

  getSubscriptionPriceDetails(uniqueName: string) {
    const documentKey = Object.keys(this.subscriptionPrices).find((key) =>
      key.toLowerCase().includes(uniqueName.toLowerCase())
    );
    return documentKey ? this.subscriptionPrices[documentKey as SubscriptionPriceEnum] : null;
  }

  send(): void {
    if (!this.contactItem.subject || !this.contactItem.email || !this.contactItem.content) {
      this.snackBar.openFromComponent(ErrorComponent, {
        data: { message: 'Vă rugăm să completați toate câmpurile.' },
        duration: 2000,
      });
      return;
    }

    this.isLoading = true;
    this.contactSuccess = false;
    this.contactItem.date = new Date();

    this.contactUsService
      .addItem(this.contactItem)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        () => {
          this.contactItem.subject = '';
          this.contactItem.email = '';
          this.contactItem.content = '';
          this.contactSuccess = true;
          this.snackBar.openFromComponent(InformationComponent, {
            data: { message: 'Mesajul a fost trimis cu succes!' },
            duration: 2000,
          });
        },
        () => {
          this.snackBar.openFromComponent(ErrorComponent, {
            data: { message: 'A apărut o eroare. Vă rugăm să încercați din nou.' },
            duration: 2000,
          });
        }
      );
  }

  navigateToBlogPost(id: number): void {
    this.router.navigate(['/blog-post'], { queryParams: { id } });
  }
}
